import { Component, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TableModule } from 'primeng/table';
import { PanelModule } from '../../../modules/panel.module';
import { OrderService } from '../../../services/order.service';
import { UserService } from '../../../services/user.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'brt-transact-details',
  standalone: true,
  imports: [PanelModule, TableModule],
  templateUrl: './transact-details.component.html',
  styleUrl: './transact-details.component.css'
})
export class TransactDetailsComponent {
  basePath = this.route.snapshot.data['basePath'] || '/';
  constructor(private route: ActivatedRoute, private router: Router, private orderService: OrderService, userService: UserService) {
    route.params.subscribe(async params => {
      this.previewPeriod = {};
      this.businessId = params['bid'];
      this.transactId = params['tid'];
      await this.load();
    });
    userService.current$.pipe(takeUntilDestroyed()).subscribe(user => {
      this.isAdmin = user?.Permissions?.includes('manage-payments');
    });
  }

  @Input() mode: 'simple' | 'full' = 'full';
  loading: boolean;
  businessId: any;
  transactId: any;
  transact: any;
  orders: any[];
  total: number;
  limit = 100;
  isAdmin: boolean;

  periodStart = '';
  periodEnd = '';
  paidDate = '';
  paidAmount: number | null = null;
  paymentReference = '';
  previewPeriod: any = {};
  error = '';

  get isCurrent() { return !this.transactId || this.transactId === 'current'; }

  async preview() {
    if (!this.periodStart || !this.periodEnd || this.periodEnd < this.periodStart) {
      this.error = 'Select a valid start and end date.';
      return;
    }
    const end = new Date(this.periodEnd + 'T00:00:00');
    end.setDate(end.getDate() + 1);
    this.previewPeriod = {
      periodStart: new Date(this.periodStart + 'T00:00:00').toISOString(),
      periodEnd: end.toISOString()
    };
    await this.load();
  }

  async previousWeek() {
    const end = new Date();
    end.setHours(0, 0, 0, 0);
    end.setDate(end.getDate() - (end.getDay() + 6) % 7);
    const start = new Date(end);
    start.setDate(start.getDate() - 7);
    end.setDate(end.getDate() - 1);
    const date = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    this.periodStart = date(start);
    this.periodEnd = date(end);
    await this.preview();
  }

  async recordSettlement() {
    if (!Object.keys(this.previewPeriod).length) return;
    if (!this.validPayment()) return;
    this.loading = true;
    this.error = '';
    try {
      await this.orderService.createTransact(this.businessId, {
        ...this.previewPeriod,
        ...(this.paidDate ? { paidAmount: this.paidAmount, paidDate: new Date(this.paidDate).toISOString() } : {}),
        paymentReference: this.paymentReference
      });
      await this.router.navigate([this.basePath, 'transact', this.businessId]);
    } catch {
      this.error = 'Settlement could not be saved. Reload and check the transaction list before retrying.';
    } finally { this.loading = false; }
  }

  private async load() {
    this.loading = true;
    this.transact = await this.orderService.getTransact(this.businessId, this.isCurrent ? undefined : this.transactId, this.previewPeriod);
    if (!this.isCurrent && this.transact) {
      this.paidAmount = this.transact.RecordedPaidAmount;
      this.paymentReference = this.transact.PaymentReference || '';
      const paid = this.transact.PaidDate ? new Date(this.transact.PaidDate) : null;
      this.paidDate = paid ? new Date(paid.getTime() - paid.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '';
    }
    if (this.mode === 'full')
      await this.loadOrders();
    this.loading = false;
  }

  async loadOrders(event?: any) {
    this.loading = true;
    const details = await this.orderService.getTransactOrders(this.businessId, this.isCurrent ? undefined : this.transactId, event?.first || 0, event?.rows || this.limit, this.previewPeriod);
    this.orders = details.List;
    this.total = details.Total;
    this.loading = false;
  }

  async withdraw() {
    this.loading = true;
    await this.orderService.createTransact(this.businessId);
    this.router.navigate([this.basePath, 'transact', this.businessId]);
    this.loading = false;
  }

  validPayment() {
    const hasAmount = this.paidAmount !== null && this.paidAmount !== undefined;
    if ((hasAmount && (!Number.isFinite(this.paidAmount) || this.paidAmount < 0 || Math.abs(this.paidAmount * 100 - Math.round(this.paidAmount * 100)) > 0.000001)) ||
        (this.isCurrent && hasAmount !== !!this.paidDate) ||
        (!this.isCurrent && !hasAmount) || (hasAmount && this.paidAmount > 0 && !this.paidDate)) {
      this.error = 'Enter the actual total paid amount (maximum two decimal places) and payment date. Leave both empty only to create a pending settlement.';
      return false;
    }
    return true;
  }

  async savePayment() {
    if (!this.validPayment()) return;
    this.loading = true;
    this.error = '';
    try {
      await this.orderService.saveTransact({
        ...this.transact,
        PaidAmount: this.paidAmount,
        PaidDate: this.paidDate ? new Date(this.paidDate).toISOString() : null,
        PaymentReference: this.paymentReference
      });
      await this.load();
    } catch {
      this.error = 'Payment could not be saved. Reload and check the recorded amount before retrying.';
    } finally { this.loading = false; }
  }
}
